import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {InputModule} from "../../modules/input/input.module";
import {MixtureService} from "../../services/mixture.service";
import { CommonModule, NgOptimizedImage } from "@angular/common";
import {ProductTableComponent} from "../../components/product-table/product-table.component";
import {Mixture} from "../../data/mixtures";
import {FormControl, FormGroup} from "@angular/forms";
import {SliderWithNumberComponent} from "../../modules/input/slider-with-number/slider-with-number.component";
import {ActivatedRoute, Router} from "@angular/router";
import {SelectComponent} from "../../modules/input/select/select.component";

@Component({
    selector: 'app-mixing',
  imports: [
    CommonModule,
    InputModule,
    ProductTableComponent,
    NgOptimizedImage,
    NgOptimizedImage
  ],
    templateUrl: './mixing.page.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './mixing.page.scss'
})
export class MixingPage implements OnInit {
  mixtureOptions = this.getMixtureOptions()
  matchingProducts: Mixture[] = []
  similarProducts: Mixture[] = []
  total = 0
  selectedMixtureOption?: {value: number, label: string} = undefined

  @ViewChild(SelectComponent) selectComponent?: SelectComponent
  @ViewChild('propaneInput') propaneInput?: SliderWithNumberComponent
  @ViewChild('isoButaneInput') isoButaneInput?: SliderWithNumberComponent
  @ViewChild('nButaneInput') nButaneInput?: SliderWithNumberComponent

  form = new FormGroup({
    propane: new FormControl(0 as number|null),
    isoButane: new FormControl(0 as number|null),
    nButane: new FormControl(0 as number|null),
    pressure: new FormControl(null as number|null)
  })

  constructor(private mixtureService: MixtureService,
              private activatedRoute: ActivatedRoute,
              private router: Router) {
  }

  ngOnInit() {
    this.loadNameFromURLFragment(this.activatedRoute.snapshot.fragment)

    this.activatedRoute.fragment.subscribe((fragment) => {
      this.loadNameFromURLFragment(fragment)
    })

    this.form.valueChanges.subscribe((value) => {
      this.calculatePressure()

      this.propaneInput?.setValue(value.propane!)
      this.isoButaneInput?.setValue(value.isoButane!)
      this.nButaneInput?.setValue(value.nButane!)

      this.total = this.calculateTotal()
    })
  }

  isHundredPercent() {
    return Number(this.form.value.propane)
      + Number(this.form.value.isoButane) +
      + Number(this.form.value.nButane) === 100
  }

  loadNameFromURLFragment(fragment: string|null) {
    if (fragment === null) {
      return
    }

    if (!/^drivosol-[0-9A-Z]+$/.test(fragment)) {
      return
    }

    const [_unused, name] = fragment.split('-')

    const mixture = this.mixtureService.getMixtureByName(name)

    if (mixture === undefined) {
      this.selectedMixtureOption = undefined
      return
    }

    setTimeout(() => {
      this.selectedMixtureOption = {value: mixture.id, label: `Drivosol ${mixture.name}`}
      this.setupMixture(mixture)
    })
  }

  calculatePressure() {
    const pressure = this.mixtureService.calculatePressure(
      this.form.value.propane!,
      this.form.value.isoButane!,
      this.form.value.nButane!
    )

    this.form.patchValue({pressure}, {emitEvent: false})

    setTimeout(() => {
      this.findProducts()
    })
  }

  getMixtureOptions() {
    return this.mixtureService.getMixtures().map((mixture) => {
      return {value: mixture.id, label: `Drivosol ${mixture.name}`}
    })
  }

  onSelectMixture(mixtureId: any) {
    const mixture = this.mixtureService.getMixtureById(mixtureId)

    if (mixture === undefined) {
      return
    }

    this.setupMixture(mixture)
  }

  setupMixture(mixture: Mixture) {
    this.form.patchValue({
      propane: mixture.propane,
      isoButane: mixture.isobutane,
      nButane: mixture.butane,
      pressure: mixture.bar
    })

    window.location.hash = `drivosol-${mixture.name}`
  }

  openMail() {
    const to = 'drivosol@evonik.com'
    const subject = this.getLocalizedEmailSubject()
    const message = this.getLocalizesEmailMessage()

    const link = document.createElement('a')
    link.href = `mailto:${to}?subject=${subject}&body=${message}`
    link.click()
  }

  getLocalizedEmailSubject() {
    if ($localize.locale === 'de') {
      return 'DRIVOSOL: Anfrage für eigene Mischung'
    }

    return 'DRIVOSOL: Inquiry for own mixture'
  }

  getLocalizesEmailMessage() {
    if ($localize.locale === 'de') {
      return [`Propan: ${this.form.value.propane} %`, `Iso-Butan: ${this.form.value.isoButane} %`, `N-Butan: ${this.form.value.nButane} %`, `Druck: ${this.form.value.pressure} bar`].join('%0D%0A')

    }

    return [`Propane: ${this.form.value.propane} %`, `Iso-Butane: ${this.form.value.isoButane} %`, `N-Butane: ${this.form.value.nButane} %`, `Pressure: ${this.form.value.pressure} bar`].join('%0D%0A')

  }

  onChangeValue(value: number, type: 'propane'|'isoButane'|'nButane') {
    this.form.get(type)?.patchValue(value)

    // wenn genau ein wert 0 ist, dann berechne den wert mit den anderen beiden

    if (value === 0) {
      return
    }

    const zeroValues = []
    const nonZeroValues = []

    for (const attribute of ['propane', 'isoButane', 'nButane']) {
      const control = this.form.get(attribute)
      const value = control?.value ?? 0;

      if (value === 0) {
        zeroValues.push({value, control})
      } else {
        nonZeroValues.push({value, control})
      }
    }

    if (zeroValues.length === 1 && nonZeroValues.length === 2) {
      const sum = parseFloat((100 - nonZeroValues[0].value - nonZeroValues[1].value).toFixed(1))

      if (sum < 100 && sum > 0) {

        zeroValues[0].control?.setValue(sum)
      }
    }

  }

  calculateTotal() {
    return this.form.value.propane! + this.form.value.isoButane! + this.form.value.nButane!
  }

  findProducts() {
    const propane = this.form.value.propane!
    const isoButane = this.form.value.isoButane!
    const nButane = this.form.value.nButane!
    const pressure = this.form.value.pressure!

    const {matchingProducts, similarProducts} = this.mixtureService.findProductsForValues(propane, isoButane, nButane, pressure)

    this.matchingProducts = matchingProducts
    this.similarProducts = similarProducts
  }

  selectMixtureOption(e: Event) {
    const select = e.target as HTMLSelectElement
    this.selectedMixtureOption = this.mixtureOptions.find(o => o.value === +select.value)
    if (this.selectedMixtureOption !== undefined) {
      this.onSelectMixture(this.selectedMixtureOption.value)
    }
  }

  openPressure() {
    if (this.form.value.pressure) {
      const pressure = this.form.value.pressure
      this.router.navigate([$localize`:@@link_pressure:/pressure`], {fragment: `bar-${pressure}`}).then()
    }
  }
}
